import type { AttemptedOoxmlPair, RedlineFailure, RedlineFailureReport, RedlineFailureStage } from "./redline-failure";
import { readMergeStep, readWordError } from "./redline-failure";
import type { RedlineApplyContext } from "./redline-snapshot";
import { reportError } from "./sentry-reporter";

export const SENTRY_TAG_ERROR_TYPE = "error_type";
export const SENTRY_TAG_FAILURE_STAGE = "redline_failure_stage";
export const SENTRY_TAG_MERGE_STEP = "merge_step";
export const SENTRY_TAG_SNAPSHOT_ID = "redline_snapshot_id";
export const SENTRY_TAG_SNAPSHOT_STATUS = "snapshot_status";

export const REDLINE_FAILURE_ERROR_TYPE = "ooxml_merge_failure";

export function redlineFailureFingerprint(stage: RedlineFailureStage): string[] {
    return [REDLINE_FAILURE_ERROR_TYPE, stage];
}

function measureOoxmlLengths(pair: AttemptedOoxmlPair) {
    return {
        attemptedOoxmlLength: pair.attemptedOoxml.length,
        currentRangeOoxmlLength: pair.currentRangeOoxml.length,
    };
}

function describeRedlineFailureForSentry({
    applyContext,
    failure,
}: {
    applyContext: RedlineApplyContext;
    failure: RedlineFailure;
}): Record<string, unknown> {
    const { capture } = applyContext;
    const common = {
        capturedBase64Length: capture.status === "captured" || capture.status === "too_large" ? capture.base64Length : null,
        wordError: readWordError(failure),
    };
    switch (failure.stage) {
        case "range_unresolved":
        case "bookmark_range_null":
            return {
                ...common,
                bookmarks: failure.bookmarks,
                attemptedOoxmlLength: failure.attemptedOoxml.length,
            };
        case "invalid_bookmark_name":
            return {
                ...common,
                bookmarks: failure.bookmarks,
                invalidBookmarks: failure.invalidBookmarks,
                attemptedOoxmlLength: failure.attemptedOoxml.length,
            };
        case "untracked_write_detected":
            return {
                ...common,
                bookmarks: failure.bookmarks,
                ...measureOoxmlLengths(failure.ooxml),
                lostParagraphs: failure.lostParagraphs,
                addedParagraphs: failure.addedParagraphs,
            };
        case "merge_failed":
            return {
                ...common,
                bookmarks: failure.bookmarks,
                ...measureOoxmlLengths(failure.ooxml),
            };
        case "backend_repair_exhausted":
            return {
                ...common,
                bookmarks: failure.bookmarks,
                ...measureOoxmlLengths(failure.ooxml),
                repairAttempts: failure.repairAttempts,
                repairFailureMessage: failure.repairFailureMessage,
            };
        case "agent_recovery_threw":
            return {
                ...common,
                bookmarks: failure.bookmarks,
                ...measureOoxmlLengths(failure.ooxml),
                agentFailureMessage: failure.agentFailureMessage,
            };
        case "agent_recovery_drifted":
            return {
                ...common,
                bookmarks: failure.bookmarks,
                ...measureOoxmlLengths(failure.ooxml),
                driftDistance: failure.drift.distance,
                driftReason: failure.drift.reason,
                expectedTextLength: failure.drift.expectedText.length,
                actualTextLength: failure.drift.actualText.length,
                divergenceOffset: failure.drift.divergenceOffset,
            };
        case "execute_js_failed":
            return common;
    }
}

export interface CaptureRedlineFailureEventArgs {
    applyContext: RedlineApplyContext;
    failure: RedlineFailure;
    error: Error;
    report: RedlineFailureReport;
}

export function captureRedlineFailureEvent({
    applyContext,
    failure,
    error,
    report,
}: CaptureRedlineFailureEventArgs): void {
    const mergeStep = readMergeStep(failure);
    const tags: Record<string, string> = {
        [SENTRY_TAG_ERROR_TYPE]: REDLINE_FAILURE_ERROR_TYPE,
        [SENTRY_TAG_FAILURE_STAGE]: failure.stage,
        [SENTRY_TAG_SNAPSHOT_STATUS]: report.snapshotStatus,
    };
    if (mergeStep) tags[SENTRY_TAG_MERGE_STEP] = mergeStep;
    if (report.snapshotId) tags[SENTRY_TAG_SNAPSHOT_ID] = report.snapshotId;

    reportError(failure.stage === "execute_js_failed" ? new Error(failure.stage) : error, {
        level: "error",
        fingerprint: redlineFailureFingerprint(failure.stage),
        tags,
        contexts: { redline_failure: describeRedlineFailureForSentry({ applyContext, failure }) },
    });
}
